/* eslint-disable import/no-cycle */
import React from 'react';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import ChevronRight from '@mui/icons-material/ChevronRightRounded';
import { format } from 'date-fns';
import { PropsOf } from '@emotion/react';
import { formatDollarAmount } from '../../utils/global/helper';
import TransactionBadge from './TransactionBadge';
import { Transaction } from '../../interfaces';
import ListItemSkeleton from './ListItemSkeleton';
import TransactionDetailModal from './TransactionDetailModal';

interface Props {
  transactions: Transaction[];
  isLoading?: boolean;
  modalEnabled?: boolean;
}

function TransactionButton(props: PropsOf<typeof Button>) {
  return <Button {...props} disableRipple />;
}

function TransactionsList({
  transactions,
  isLoading,
  modalEnabled = true,
}: Props) {
  const theme = useTheme();

  const [selectedTransaction, setSelectedTransaction] =
    React.useState<Transaction | null>(null);

  const isCardTx = (transaction: Transaction) =>
    ['CREDIT', 'TERMINAL'].includes(transaction.type);

  const IndividualTxComponent = modalEnabled ? TransactionButton : Box;

  const onClickTransaction = (transaction: Transaction) => {
    if (!modalEnabled) return;
    setSelectedTransaction(transaction);
  };
  return (
    <>
      <Stack spacing={0} width="100%">
        {!isLoading && (
          <>
            {transactions?.map((transaction, index) => {
              const isFirst = index === 0;
              const isLast = index === transactions.length - 1;
              const isCard = isCardTx(transaction);
              const isGiftCard = transaction.type === 'GIFT_CARD';
              return (
                <IndividualTxComponent
                  key={transaction.id}
                  onClick={() => onClickTransaction?.(transaction)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    width: '100%',
                    textAlign: 'left',
                    padding: 0,
                    margin: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                    borderBottom: !isLast
                      ? `1px solid ${theme.palette.lightGrey.main}`
                      : undefined,
                  }}>
                  <Box
                    width="100%"
                    py={isFirst ? 0 : 1.5}
                    pb={isFirst ? 1.5 : undefined}
                    key={transaction.id}>
                    <Typography variant="h5">
                      {formatDollarAmount(transaction.amount)}{' '}
                      {isCard
                        ? transaction.status === 'SUCCESS'
                          ? 'paid by card'
                          : transaction.status === 'FAILED' || transaction.error
                          ? 'payment failed'
                          : transaction.status === 'REFUNDED'
                          ? 'payment refunded'
                          : 'payment incomplete'
                        : isGiftCard
                        ? 'paid by gift card'
                        : 'paid in cash/other'}
                    </Typography>
                    <Typography variant="body2">
                      {format(transaction.dateCreated, 'MMM d')} at{' '}
                      {format(transaction.dateCreated, 'h:mm a')}{' '}
                      {transaction.tipAmount &&
                      (!isCard || transaction.status === 'SUCCESS')
                        ? `(incl. ${formatDollarAmount(
                            transaction.tipAmount,
                          )} tip)`
                        : null}
                      {transaction.chargeReason === 'reschedule'
                        ? `(rescheduling fee)`
                        : null}
                    </Typography>
                    <TransactionBadge
                      transaction={transaction}
                      tooltipOffset={[0, -5]}
                      light={false}
                      sx={{
                        mt: 0.5,
                        width: 'fit-content',
                      }}
                    />
                  </Box>
                  {modalEnabled && (
                    <Box
                      height="100%"
                      display="flex"
                      alignItems="center"
                      color={theme.palette.outlineGrey.main}>
                      <ChevronRight />
                    </Box>
                  )}
                </IndividualTxComponent>
              );
            })}

            {!transactions?.length && (
              <Typography variant="body2">No transactions to show</Typography>
            )}
          </>
        )}

        {isLoading && <ListItemSkeleton />}
      </Stack>
      {modalEnabled && selectedTransaction && (
        <TransactionDetailModal
          onClose={() => setSelectedTransaction(null)}
          open={!!selectedTransaction}
          transaction={selectedTransaction}
        />
      )}
    </>
  );
}

export default TransactionsList;
